import {
  addToWishlist,
  removeFromWishlist,
  type RootState,
  useAppDispatch,
  useAppSelector,
} from '@debijenkorf/app-shell';
import { type ceresCatalogTypes, type wishlistServiceTypes } from '@debijenkorf/dbk-common';

import { type Product as LunaProduct } from '@/types/api/product';
import {
  type GA4ProductItemInput,
  type MapListProductToGA4ItemArgs,
} from '@/utilities/tracking/ga4ProductModel';
import { trackWishlistChange } from '@/utilities/tracking/wishlistTracking';

type CatalogProduct = ceresCatalogTypes.components['schemas']['Product'];

// TODO: remove catalog references when all use cases are migrated e.g. plp, carousels etc.
// type Product from Luna and type Product from Catalog are not normalized - we use both
// migrated components pass a flat { code, sku } instead
type SkuProduct = { code: string; sku: string };
type WishlistProduct = LunaProduct | CatalogProduct | SkuProduct;
type AnalyticsData<Product> = {
  location?: string;
  listId?: string;
  listName?: string;
  mapProductToGA4Item?: (args: MapListProductToGA4ItemArgs<Product>) => GA4ProductItemInput;
};

type UseWishlistArgs<Product extends WishlistProduct> = {
  products: Product[] | Product;
  analyticsData?: AnalyticsData<Product>;
};

const getVariantCode = (product: WishlistProduct) =>
  'sku' in product ? product.sku : product.currentVariantProduct?.code;

type WishlistCombinedProductCodes =
  wishlistServiceTypes.components['schemas']['VariantMasterCodes'][];
type OnWishlistChange = (index: number) => (type: 'add' | 'remove', code?: string) => void;
type OnSingleWishlistChange = (type: 'add' | 'remove', code?: string) => void;

export type UseWishlistReturn = {
  combinedProductCodes: WishlistCombinedProductCodes;
  isSelected: (product: WishlistProduct) => boolean;
  onWishlistChange: OnWishlistChange;
  onSingleWishlistChange: OnSingleWishlistChange;
};

export const useWishlist = <Product extends WishlistProduct>({
  products,
  analyticsData: { location, listId, listName, mapProductToGA4Item } = {},
}: UseWishlistArgs<Product>): UseWishlistReturn => {
  const dispatch = useAppDispatch();
  const combinedProductCodes = useAppSelector(
    (state: RootState) => state.wishlist.combinedProductCodes,
  ) as WishlistCombinedProductCodes;

  const isSelected = (product: WishlistProduct) => {
    return combinedProductCodes.some(
      ({ productVariantCode }) => productVariantCode === getVariantCode(product),
    );
  };

  const handleWishlistChange = (type: 'add' | 'remove', code?: string, position: number = 1) => {
    const productArray = Array.isArray(products) ? products : [products];
    const product = productArray.find(item => item && getVariantCode(item) === code);
    if (!product) return;

    const wishlistProduct = product as CatalogProduct | SkuProduct;

    // pass new tracking conditionally until all use cases are migrated
    const onTracking = mapProductToGA4Item
      ? () =>
          trackWishlistChange({
            type,
            item: mapProductToGA4Item({
              product,
              locationId: location ?? '',
              index: position,
              itemListId: listId,
              itemListName: listName,
            }),
          })
      : undefined;

    if (type === 'add') {
      dispatch(addToWishlist(wishlistProduct, location, listId, listName, position, onTracking));
    } else {
      dispatch(removeFromWishlist(wishlistProduct, undefined, location, onTracking));
    }
  };

  const onWishlistChange: OnWishlistChange = index => (type, code) =>
    handleWishlistChange(type, code, index);

  const onSingleWishlistChange: OnSingleWishlistChange = (type, code) =>
    handleWishlistChange(type, code, 1);

  return {
    combinedProductCodes,
    isSelected,
    onWishlistChange,
    onSingleWishlistChange,
  };
};
